import React, { useCallback, useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { AsnDisplay, SubHeader, ToolsListView } from "../../components";
import {
  AGGREGATES,
  RESOURCE_TYPE,
  USER_OPERATIONS,
  TOOLS,
  CHASSIS_COLUMN,
  AGGREGATE_SUBCOLUMN,
  CHASSIS_SUBCOLUMN,
  JLR_PLANT,
  APPLICATION_VARIANTS,
  MASTER_SUBCOLUMN,
  APPROVAL_SUBCOLUMN,
  ENGINE_SUBCOLUMN,
} from "../../constants";
import styles from "./LandingPage.module.css";
import sparqplug from "../../images/sparqplug.png";
import time from "../../images/time.svg";
import asnReport from "../../images/report_icon.png";
import {
  debugLog,
  getAggregatePlanUrl,
  getAggregateUrl,
  getAggregateLineUpUrl,
  getEmergencyAgSNUrl,
  getAggregateDispatchUrl,
  getAggregateAvailabilityUrl,
  getAgSNOverviewUrl,
  getInterplantAggregateAssignment,
  getFreePoolAggregateUrl,
} from "../../utils/utils";
import {
  getLastAsnGeneratedDateForPlantAndLine,
  fetchEmergencyAgSNCount,
  fetchLinesForGivenPlant,
  getSubAssemblyConfigurations,
} from "../../apis/calls";

import { usePlantAndLinesStore } from "../../providers/PlantAndLinesStore/PlantAndLinesStore";
import withAllowedOperationsProvider, {
  useAllowedOperationsContext,
} from "../../hocs/withAllowedOperationsProvider/withAllowedOperationsProvider";
import config from "../../config/config";
import { ListView } from "../../components";

const LandingPage = () => {
  const {
    PRODUCTION_MASTER_DATA_UI_URL,
    APPLICATION_VARIANT,
    SPAR_Q_PLUG_BASE_URL,
  } = config;
  const history = useHistory();
  const [plant] = usePlantAndLinesStore().plantState;
  const [line] = usePlantAndLinesStore().lineState;
  const { aggregatesWithAccessToDispatch } = usePlantAndLinesStore();
  const [lineOptions] = usePlantAndLinesStore().lineOptions;
  const [isAsnDisplayOpen, setIsAsnDisplayOpen] = useState(false);
  const [endDate, setEndDate] = useState(null);
  const [count, setCount] = useState({});

  const [selectedColumnsQueue, setSelectedColumsQueue] = useState([]);

  const [allPlantLines, setAllPlantLines] = useState([]);
  const [subAssemblyConfigsMap, setSubAssemblyConfigsMap] = useState({});

  const openAsnDisplay = () => setIsAsnDisplayOpen(true);
  const closeAsnDisplay = () => setIsAsnDisplayOpen(false);

  useEffect(() => {
    if (!plant?.value) return;
    fetchLinesForGivenPlant(plant.value)
      .then((res) => {
        if (res && res.data) {
          setAllPlantLines(res.data);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch lines for plant in LandingPage", err);
      });
  }, [plant.value]);

  const isLucknowProlifePlant = plant?.label === "Lucknow-Prolife";
  const pathName = isLucknowProlifePlant ? "/engine" : "/chassis";

  const allowedOperations = useAllowedOperationsContext()
    ?.allowedOperationsWithValue;

  async function loadEndDateForPlantAndLine(plant, line) {
    try {
      const lastAsnGeneratedDate = await getLastAsnGeneratedDateForPlantAndLine(
        plant.value,
        line.value,
      );
      setEndDate(lastAsnGeneratedDate);
    } catch (error) {
      debugLog("failed to get lastAsnGeneratedDate in landing page: ", error);
      setEndDate(null);
    }
  }

  const getToolOptions = () => {
    if (plant.label === "Lucknow-Prolife") {
      return [
        {
          label: TOOLS.ASN_OVERVIEW,
          authOperation: USER_OPERATIONS.GET_ASN_OVERVIEW,
          action: () => {
            history.push("/asn-live");
          },
        },
        {
          label: TOOLS.MANUFACTURING_INSIGHTS,
          authOperation: USER_OPERATIONS.GET_MANUFACTURING_INSIGHTS,
          image: (
            <img src={sparqplug} className={styles.sparqplug} alt="SparQPlug" />
          ),
          action: () => {
            if (SPAR_Q_PLUG_BASE_URL)
              window.open(SPAR_Q_PLUG_BASE_URL, "_target");
          },
        },
        {
          label: TOOLS.MASTER_DATA_MANAGEMENT,
          authOperation: USER_OPERATIONS.GET_MASTER_DATA,
          action: () => {
            window.open(PRODUCTION_MASTER_DATA_UI_URL, "_target");
          },
        },
      ];
    } else if (APPLICATION_VARIANT === APPLICATION_VARIANTS.MES4_EV) {
      return [
        {
          label: TOOLS.PRODUCTION_REVIEW,
          authOperation: USER_OPERATIONS.GET_PRODUCTION_REVIEW,
          action: () => {
            history.push("/production-review");
          },
        },
        {
          label: TOOLS.ASN_OVERVIEW_REPORT,
          authOperation: USER_OPERATIONS.GET_ASN_OVERVIEW,
          image: (
            <img
              src={asnReport}
              className={styles.sparqplug}
              alt="ASN_REPORT"
            />
          ),
          action: () => {
            history.push("/asn-overview-report");
          },
        },
        {
          label: TOOLS.MASTER_DATA_MANAGEMENT,
          authOperation: USER_OPERATIONS.GET_MASTER_DATA,
          action: () => {
            window.open(PRODUCTION_MASTER_DATA_UI_URL, "_target");
          },
        },
      ];
    } else {
      return [
        {
          label: TOOLS.ASN_OVERVIEW,
          authOperation: USER_OPERATIONS.GET_ASN_OVERVIEW,
          action: () => {
            history.push("/asn-live");
          },
        },
        {
          label: TOOLS.DROPPING_UPDATE,
          authOperation: USER_OPERATIONS.GET_DROPPING_UPDATE,
          action: () => {
            history.push("/operator");
          },
        },
        {
          label: TOOLS.MANUFACTURING_INSIGHTS,
          authOperation: USER_OPERATIONS.GET_MANUFACTURING_INSIGHTS,
          image: (
            <img src={sparqplug} className={styles.sparqplug} alt="SparQPlug" />
          ),
          action: () => {
            if (SPAR_Q_PLUG_BASE_URL)
              window.open(SPAR_Q_PLUG_BASE_URL, "_target");
          },
        },
        {
          label: TOOLS.MASTER_DATA_MANAGEMENT,
          authOperation: USER_OPERATIONS.GET_MASTER_DATA,
          action: () => {
            window.open(PRODUCTION_MASTER_DATA_UI_URL, "_target");
          },
        },
        {
          label: TOOLS.SHIFT_CONFIGURATOR,
          authOperation: USER_OPERATIONS.GET_MASTER_SHIFT_CONFIGURATOR,
          image: (
            <div
              style={{
                backgroundColor:
                  plant.label === JLR_PLANT ? "#26662E" : "#2462ac",
                borderRadius: "100%",
                height: "32px",
                width: "32px",
              }}
            >
              <img src={time} className={styles.sparqplug} alt="time" />
            </div>
          ),
          action: () => {
            history.push("/shift-configurator");
          },
        },
        ...(plant.label === "Pantnagar"
          ? [
              {
                label: TOOLS.ENGINE_CONFIRMATION_UPDATE,
                authOperation: USER_OPERATIONS.GET_DROPPING_UPDATE,
                action: () => {
                  history.push("/Engine-Confirmation");
                },
              },
              {
                label: TOOLS.PBS_DROPPING_CONFIRMATION,
                authOperation: USER_OPERATIONS.UPDATE_CHASSIS_PLAN_ASN,
                action: () => {
                  history.push("/pbs-dropping-confirmation");
                },
              },
            ]
          : []),
        {
          label: TOOLS.EVR_CONFIGURATOR,
          authOperation: USER_OPERATIONS.GET_ALL_EVR,
          action: () => {
            history.push("/evr-number-configurator");
          },
        },
        {
          label: TOOLS.MASTER_CAPACITY_CONFIGURATOR,
          authOperation: USER_OPERATIONS.GET_ALL_EVR,
          next: getNextLaneForMaster(),
          level: 1,
          customLabel: styles.optionMenuLabel,
          action: null,
          //isDisplayable: isShopTypeTabDisplayable(master, allowedOperations)
        },
        {
          label: TOOLS.SUB_ASSEMBLY_CONFIGURATOR,
          authOperation: USER_OPERATIONS.GET_ALL_EVR,
          action: () => {
            history.push("/sub-assembly-configurator");
          },
        },
      ];
    }
  };

  const toolOptions = getToolOptions();

  const handleSelection = (selection) => {
    const lineCode = selection?.value;

    setSelectedColumsQueue((prev) => {
      if (prev.length) {
        const lastSelection = prev[prev.length - 1];
        if (lastSelection === selection) return prev;
        while (prev.length) {
          const existingTopLevel = prev[prev.length - 1].level;
          if (existingTopLevel >= selection.level) prev.pop();
          else break;
        }
      }
      prev.push(selection);
      return [...prev];
    });

    if (
      selection?.isSubAssemblyLine &&
      lineCode &&
      subAssemblyConfigsMap[lineCode] === undefined &&
      plant?.value
    ) {
      getSubAssemblyConfigurations(plant.value, lineCode)
        .then((res) => {
          const configs =
            res && res.data && Array.isArray(res.data) ? res.data : [];
          setSubAssemblyConfigsMap((prev) => ({
            ...prev,
            [lineCode]: configs,
          }));
        })
        .catch((err) => {
          console.error(
            `Failed to fetch sub-assembly configs for line ${lineCode}`,
            err,
          );
          setSubAssemblyConfigsMap((prev) => ({
            ...prev,
            [lineCode]: [],
          }));
        });
    }
  };

  const getArrayOfOptionSetsToRender = (queue) =>
    queue
      .map((item) => {
        if (item.isSubAssemblyLine && item.value) {
          return getNextLaneForSubAssemblies(item.value);
        }
        return item.next;
      })
      .filter((item) => item);

  const getNextLaneForAggregate = (aggregate) => [
    {
      value: AGGREGATE_SUBCOLUMN.PLAN,
      label: AGGREGATE_SUBCOLUMN.PLAN,
      customLabel: styles.optionMenuLabel,
      level: 2,
      next: null,
      action: () => {
        history.push(getAggregatePlanUrl(aggregate));
      },
      isDisplayable: true,
    },
    {
      value: AGGREGATE_SUBCOLUMN.LINE_UP,
      label: AGGREGATE_SUBCOLUMN.LINE_UP,
      level: 2,
      next: null,
      action: () => {
        history.push(getAggregateLineUpUrl(aggregate));
      },
      isDisplayable: true,
    },
    {
      value: AGGREGATE_SUBCOLUMN.AVAILABILITY,
      label: AGGREGATE_SUBCOLUMN.AVAILABILITY,
      level: 2,
      customLabel: styles.optionMenuLabel,
      next: null,
      action: () => {
        history.push(getAggregateAvailabilityUrl(aggregate));
      },
      isDisplayable: true,
    },
    {
      value: AGGREGATE_SUBCOLUMN.DISPATCH,
      label: AGGREGATE_SUBCOLUMN.DISPATCH,
      level: 2,
      customLabel: styles.optionMenuLabel,
      next: null,
      action: () => {
        history.push(getAggregateDispatchUrl(aggregate));
      },
      isDisplayable: true,
    },
    {
      value: AGGREGATE_SUBCOLUMN.EMERGENCY_AGSN,
      label: AGGREGATE_SUBCOLUMN.EMERGENCY_AGSN,
      level: 2,
      next: null,
      action: () => {
        history.push(getEmergencyAgSNUrl(aggregate));
      },
      isDisplayable: true,
    },
    {
      value: AGGREGATE_SUBCOLUMN.AGSN_OVERVIEW,
      label: AGGREGATE_SUBCOLUMN.AGSN_OVERVIEW,
      level: 2,
      next: null,
      action: () => {
        history.push({
          pathname: getAgSNOverviewUrl(aggregate),
          state: { aggregate },
        });
      },
      isDisplayable: true,
    },
    {
      value: AGGREGATE_SUBCOLUMN.INTERPLANT_AGGREGATE_ASSIGNMENT,
      label: AGGREGATE_SUBCOLUMN.INTERPLANT_AGGREGATE_ASSIGNMENT,
      level: 2,
      next: null,
      action: () => {
        history.push({
          pathname: getInterplantAggregateAssignment(aggregate),
          state: { aggregate },
        });
      },
      isDisplayable: true,
    },
    {
      value: AGGREGATE_SUBCOLUMN.SUB_ASSEMBLY_VIEW,
      label: AGGREGATE_SUBCOLUMN.SUB_ASSEMBLY_VIEW,
      level: 2,
      customLabel: styles.optionMenuLabel,
      next: getNextLaneForSubAssemblyLines(aggregate),
      action: null,
      isDisplayable: true,
    },
    {
      value: AGGREGATE_SUBCOLUMN.SUB_ASSEMBLY_OVERVIEW,
      label: AGGREGATE_SUBCOLUMN.SUB_ASSEMBLY_OVERVIEW,
      level: 2,
      customLabel: styles.optionMenuLabel,
      next: getNextLaneForSubAssemblyOverviewLines(aggregate),
      action: null,
      isDisplayable: true,
    },
    {
      value: AGGREGATE_SUBCOLUMN.FREE_POOL_AGGREGATES,
      label: AGGREGATE_SUBCOLUMN.FREE_POOL_AGGREGATES,
      level: 2,
      customLabel: styles.optionMenuLabel,
      next:null,
      action: () => {
        history.push(getFreePoolAggregateUrl(aggregate));
      },
      isDisplayable: true,
    },
    ...(aggregate === AGGREGATES.ENGINE
      ? [
          {
            value: ENGINE_SUBCOLUMN.ENGINE_DRESSING_SEQUENCE,
            label: ENGINE_SUBCOLUMN.ENGINE_DRESSING_SEQUENCE,
            level: 2,
            next: null,
            action: () => {
              history.push("/engine-dressing-sequence");
            },
            isDisplayable: true,
          },
        ]
      : []),
  ];

  const normalizeShopName = (str) => {
    if (!str) return "";
    return str
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
  };

  const filterLinesForShop = (allLines, shopType) => {
    if (!allLines || allLines.length === 0) return [];
    if (!shopType) return allLines;

    const target = normalizeShopName(shopType);

    if (target === "tcf" || target === "chassis") {
      return allLines.filter((l) => {
        const lineShop = normalizeShopName(l.shopType);
        return lineShop === "chassis" || lineShop === "tcf";
      });
    }

    const matched = allLines.filter((l) => {
      const lineShop = normalizeShopName(l.shopType);
      return lineShop === target;
    });

    return matched;
  };

  const getNextLaneForSubAssemblies = (lineCode) => {
    const configs = subAssemblyConfigsMap[lineCode];

    if (configs === undefined) {
      return [
        {
          value: "LOADING",
          label: "Loading...",
          level: 4,
          customLabel: styles.optionMenuLabel,
          next: null,
          action: null,
          isDisplayable: true,
        },
      ];
    }

    if (configs.length === 0) {
      return [
        {
          value: "NO_DATA",
          label: "No Sub Assemblies",
          level: 4,
          customLabel: styles.optionMenuLabel,
          next: null,
          action: null,
          isDisplayable: true,
        },
      ];
    }

    return configs.map((cfg) => ({
      value: cfg.subAssemblyName,
      label: cfg.subAssemblyName,
      level: 4,
      customLabel: styles.optionMenuLabel,
      next: null,
      action: () => {
        history.push("/sub-assembly-view", {
          subAssembly: cfg.subAssemblyName,
          line: lineCode,
          plant: plant.value,
          dropStartSeq: cfg.dropStartSeq,
          dropEndSeq: cfg.dropEndSeq,
          readyForDropFlag: cfg.readyForDropFlag,
        });
      },
      isDisplayable: true,
    }));
  };

  const getNextLaneForSubAssemblyOverviewLines = (shopType) => {
    const activeLines = filterLinesForShop(allPlantLines, shopType);

    return activeLines.map((l) => {
      const lineCode = l.line || l.value || "L1";
      const rawName = l.lineName || l.label || `${lineCode} LINE`;
      let displayLabel = rawName;

      return {
        value: lineCode,
        label: displayLabel,
        level: 3,
        customLabel: styles.optionMenuLabel,
        next: null,
        action: () => {
          history.push("/sub-assembly-live", {
            isSubAssemblyOverview: true,
            selectedLine: lineCode,
            shopType,
          });
        },
        isDisplayable: true,
      };
    });
  };

  const getNextLaneForSubAssemblyLines = (shopType) => {
    const activeLines = filterLinesForShop(allPlantLines, shopType);

    return activeLines.map((l) => {
      const lineCode = l.line || l.value || "L1";
      const rawName = l.lineName || l.label || `${lineCode} LINE`;
      let displayLabel = rawName;

      return {
        value: lineCode,
        label: displayLabel,
        level: 3,
        customLabel: styles.optionMenuLabel,
        isSubAssemblyLine: true,
        next: getNextLaneForSubAssemblies(lineCode),
        action: null,
        isDisplayable: true,
      };
    });
  };

  const getNextLaneForMaterial = (aggregate) => [
    {
      value: AGGREGATE_SUBCOLUMN.DISPATCH,
      label: AGGREGATE_SUBCOLUMN.DISPATCH,
      level: 2,
      customLabel: styles.optionMenuLabel,
      next: null,
      action: () => {
        history.push(getAggregateUrl(aggregate));
      },
      isDisplayable: true,
    },
  ];

  const getNextLaneForApproval = () => {
    return [
      {
        value: "Line Out Approval",
        label: APPROVAL_SUBCOLUMN.LINE_OUT_APPROVAL,
        level: 3,
        next: null,
        action: () => {
          history.push("/line-out-approval");
        },
        isDisplayable: true,
      },
      {
        value: "Emergency ASN Approval",
        label: APPROVAL_SUBCOLUMN.EMERGENCY_ASN_APPROVAL,
        level: 3,
        next: null,
        action: () => {
          history.push("/emergency-asn-approval");
        },
        isDisplayable: true,
      },
    ];
  };
  const getNextLaneForChassis = () => [
    {
      value: "Plan",
      label: CHASSIS_SUBCOLUMN.PLAN,
      level: 2,
      customLabel: styles.optionMenuLabel,
      next: null,
      action: () => {
        history.push("/planning");
      },
      isDisplayable: true,
    },
    {
      value: "Line Up",
      label: CHASSIS_SUBCOLUMN.LINE_UP,
      level: 2,
      customLabel: styles.optionMenuLabel,
      next: null,
      action: () => {
        history.push(pathName);
      },
      isDisplayable: true,
    },
    {
      value: "View Asn",
      label: CHASSIS_SUBCOLUMN.VIEW_ASN,
      level: 2,
      customLabel: styles.optionMenuLabel,
      next: null,
      action: openAsnDisplay,
      isDisplayable: true,
    },
    {
      value: "Month Plan",
      label: CHASSIS_SUBCOLUMN.MONTH_PLAN,
      level: 2,
      customLabel: styles.optionMenuLabel,
      next: null,
      action: () => {
        history.push("/monthly-plan");
      },
      isDisplayable: true,
    },
    {
      value: "Interplant Aggregate Conflicts",
      label: CHASSIS_SUBCOLUMN.AGGREGATE_VIEW_CONFLICTS,
      level: 2,
      customLabel: styles.optionMenuLabel,
      next: null,
      action: () => {
        history.push("/aggregate-view-conflicts");
      },
      isDisplayable: true,
    },
    {
      value: "Approval",
      label: CHASSIS_SUBCOLUMN.APPROVAL,
      level: 2,
      customLabel: styles.optionMenuLabel,
      next: getNextLaneForApproval(),
      customLabel: styles.optionMenuLabel,
      action: null,
      isDisplayable: true,
    },
    {
      value: "Sub-Assembly View",
      label: CHASSIS_SUBCOLUMN.SUB_ASSEMBLY_VIEW,
      level: 2,
      customLabel: styles.optionMenuLabel,
      next: getNextLaneForSubAssemblyLines("chassis"),
      action: null,
      isDisplayable: true,
    },
    {
      value: "Sub-Assembly Overview",
      label: CHASSIS_SUBCOLUMN.SUB_ASSEMBLY_OVERVIEW,
      level: 2,
      customLabel: styles.optionMenuLabel,
      next: getNextLaneForSubAssemblyOverviewLines("chassis"),
      action: null,
      isDisplayable: true,
    },
  ];

  function getNextLaneForMaster() {
    return [
      {
        value: "Line Feature Configurator",
        label: MASTER_SUBCOLUMN.LINE_FEATURE,
        level: 2,
        customLabel: styles.optionMenuLabel,
        next: null,
        action: () => {
          history.push("/line-feature-configurator");
        },
        isDisplayable: true,
      },
      {
        value: "Line Capacity Configurator",
        label: MASTER_SUBCOLUMN.LINE_CAPACITY,
        level: 2,
        customLabel: styles.optionMenuLabel,
        next: null,
        action: () => {
          history.push("/line-capacity-configurator");
        },
        isDisplayable: true,
      },
      {
        value: "Shift Capacity Configurator",
        label: MASTER_SUBCOLUMN.SHIFT_CAPACITY,
        level: 2,
        customLabel: styles.optionMenuLabel,
        next: null,
        action: () => {
          history.push("/shift-capacity-configurator");
        },
        isDisplayable: true,
      },
      {
        value: "Ready For Drop Limit Configurator",
        label: MASTER_SUBCOLUMN.READY_FOR_DROP,
        level: 2,
        customLabel: styles.optionMenuLabel,
        next: null,
        action: () => {
          history.push("/rfd-limits-configurator");
        },
        isDisplayable: true,
      },
      {
        value: "Sub-Assembly Configurator",
        label: MASTER_SUBCOLUMN.SUB_ASSEMBLY,
        level: 2,
        customLabel: styles.optionMenuLabel,
        next: null,
        action: () => {
          history.push("/sub-assembly-configurator");
        },
        isDisplayable: true,
      },
    ];
  }

  const isShopTypeTabDisplayable = (shopType, allowedOperationsForUser) => {
    return shopType === AGGREGATES.MATERIAL
      ? allowedOperationsForUser
          .map((allowedOperation) => allowedOperation.operation)
          .includes(USER_OPERATIONS.GET_SCM_MATERIAL_TAB)
      : allowedOperationsForUser
          .map((allowedOperation) => allowedOperation.operation)
          ?.includes(USER_OPERATIONS.GET_DISPLAYABLE_SHOP_TYPES) &&
          allowedOperationsForUser
            .filter(
              (allowedOperation) =>
                allowedOperation.operation ===
                USER_OPERATIONS.GET_DISPLAYABLE_SHOP_TYPES,
            )
            .some(
              (allowedOperation) =>
                (allowedOperation.value.includes(shopType) &&
                  (allowedOperation.value.includes(`plant:${plant.value}`) ||
                    allowedOperation.value.includes(`plant:*`))) ||
                (allowedOperation.value.includes("shop_type:*") &&
                  (allowedOperation.value.includes(`plant:${plant.value}`) ||
                    allowedOperation.value.includes(`plant:*`))) ||
                allowedOperation.value === `org:tml:resource:*` ||
                allowedOperation.value === `org:tml:*` ||
                allowedOperation.value === `org:*` ||
                allowedOperation.value === "*",
            );
  };

  const getChassisColumn = () =>
    Object.values(CHASSIS_COLUMN).map((chassis) => ({
      label:
        APPLICATION_VARIANT === APPLICATION_VARIANTS.MES4_EV ||
        plant?.label === "Pantnagar"
          ? "TCF"
          : plant?.label === "Lucknow-Prolife"
          ? "Engine"
          : chassis,
      next: getNextLaneForChassis(chassis),
      level: 1,
      customLabel: styles.optionMenuLabel,
      action: null,
      isDisplayable: isShopTypeTabDisplayable(chassis, allowedOperations),
    }));

  const getMasterColumn = () =>
    Object.values(MASTER_SUBCOLUMN).map((master) => ({
      label: TOOLS.MASTER_CAPACITY_CONFIGURATOR,
      next: getNextLaneForMaster(master),
      level: 1,
      customLabel: styles.optionMenuLabel,
      action: null,
      isDisplayable: isShopTypeTabDisplayable(master, allowedOperations),
    }));

  const getAggregateColumns = () => {
    const columns = [];
    const hiddenAggregates = [
      AGGREGATES.FRAME,
      AGGREGATES.CYLINDER_HEAD,
      AGGREGATES.CAB,
      AGGREGATES.CAMSHAFT,
      AGGREGATES.CYLINDER_BLOCK,
      AGGREGATES.CRANKSHAFT,
      AGGREGATES.INTERPLANT_AGGREGATE_ASSIGNMENT,
    ];

    const hiddenProlifeAggregates = [
      AGGREGATES.FRAME,
      AGGREGATES.REAR_AXLE,
      AGGREGATES.FRONT_AXLE,
      AGGREGATES.ENGINE,
      AGGREGATES.GEAR_BOX,
      AGGREGATES.CAB,
      AGGREGATES.PAINT,
      AGGREGATES.BIW,
      AGGREGATES.MATERIAL,
    ];

    const hiddenForAll = [
      AGGREGATES.CAMSHAFT,
      AGGREGATES.CYLINDER_BLOCK,
      AGGREGATES.CRANKSHAFT,
    ];

    if (APPLICATION_VARIANT === APPLICATION_VARIANTS.MES4_EV) {
      columns.push({
        label: AGGREGATES.BIW,
        next: getNextLaneForAggregate(AGGREGATES.BIW),
        level: 1,
        customLabel: styles.optionMenuLabel,
        action: null,
        isDisplayable: isShopTypeTabDisplayable(
          AGGREGATES.BIW,
          allowedOperations,
        ),
      });
    } else {
      columns.push(
        ...Object.values(AGGREGATES)
          .filter((aggregate) => {
            if (plant.label === "Pantnagar") {
              return !hiddenAggregates.includes(aggregate);
            }
            if (plant.label === "Lucknow-Prolife") {
              return !hiddenProlifeAggregates.includes(aggregate);
            }
            return !hiddenForAll.includes(aggregate);
          })
          .map((aggregate) => ({
            label: aggregate,
            next: getNextLaneForAggregate(aggregate),
            level: 1,
            customLabel: styles.optionMenuLabel,
            action: null,
            isDisplayable: isShopTypeTabDisplayable(
              aggregate,
              allowedOperations,
            ),
          })),
      );
    }

    return columns;
  };

  const columnsRoot = useMemo(
    () => [...getChassisColumn(), ...getAggregateColumns()],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allowedOperations, plant?.value, plant?.label, allPlantLines],
  );

  useEffect(() => {
    if (plant?.value && line?.value) loadEndDateForPlantAndLine(plant, line);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line, plant]);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const response = await fetchEmergencyAgSNCount(plant.value);
        if (response.data.length) {
          const shopCountMap = Object.fromEntries(
            response.data.map((item) => [item.shop.toUpperCase(), item.count]),
          );
          setCount(shopCountMap);
        }
      } catch (error) {
        debugLog("failed to get Emergency AgSN Count ", error);
      }
    };

    fetchCount();
  }, [plant]);

  useEffect(() => {
    setSelectedColumsQueue([]);
  }, [plant.value]);

  return (
    <div className={styles.mainBody}>
      <>
        <AsnDisplay
          enableLineSelection
          open={isAsnDisplayOpen}
          handleClose={closeAsnDisplay}
          endDate={endDate}
          plant={plant.value}
          line={line.value}
          lineName={line.label}
          lineOptions={lineOptions}
        />
      </>

      <div className={styles.container}>
        <div style={{ marginBottom: "1em" }}>
          <SubHeader title="PPC" />
        </div>
        <div className={styles.columnsContainer}>
          <div
            style={{ height: "100%" }}
            className={styles.columnsContainerLeft}
          >
            <ListView
              title="Select"
              listItems={columnsRoot}
              handleSelection={handleSelection}
            />

            {selectedColumnsQueue[0]?.label !==
              TOOLS.MASTER_CAPACITY_CONFIGURATOR &&
              getArrayOfOptionSetsToRender(selectedColumnsQueue).map(
                (listItems, index) => {
                  const selectedShop = selectedColumnsQueue[0]?.label?.toUpperCase();
                  const matchedCount = count[selectedShop] || 0;
                  const updatedItems = listItems.map((item) => {
                    if (item.label.toUpperCase() === "EMERGENCY AGSN") {
                      return { ...item, count: matchedCount };
                    }
                    return item;
                  });
                  return (
                    <ListView
                      key={`${selectedColumnsQueue[0].label}-${listItems[0]?.level}`}
                      title={index === 2 ? "Action" : "Select"}
                      listItems={updatedItems}
                      handleSelection={handleSelection}
                    />
                  );
                },
              )}
          </div>
          <div>
            <ToolsListView
              listItems={toolOptions}
              handleSelection={handleSelection}
            />
            {selectedColumnsQueue[0]?.label ===
              TOOLS.MASTER_CAPACITY_CONFIGURATOR &&
              getArrayOfOptionSetsToRender(selectedColumnsQueue).map(
                (listItems, index) => {
                  const selectedShop = selectedColumnsQueue[0]?.label?.toUpperCase();
                  const matchedCount = count[selectedShop] || 0;
                  const updatedItems = listItems.map((item) => {
                    // if (item.label.toUpperCase() === "EMERGENCY AGSN") {
                    //     return { ...item, count: matchedCount };
                    // }
                    return item;
                  });
                  return (
                    <ListView
                      key={`${selectedColumnsQueue[0].label}-${listItems[0]?.level}`}
                      title={index === 2 ? "Action" : "Select"}
                      listItems={updatedItems}
                      handleSelection={handleSelection}
                    />
                  );
                },
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default withAllowedOperationsProvider(
  LandingPage,
  false,
  RESOURCE_TYPE.CHASSIS,
  RESOURCE_TYPE.PLANT,
  RESOURCE_TYPE.DISPLAYABLE_SHOP,
  RESOURCE_TYPE.DISPLAYABLE_SCM_MATERIAL_TAB,
);
